import { useState, useEffect } from 'react'
import { FiX, FiSave, FiSearch, FiTrash2, FiUpload, FiImage, FiVideo, FiLoader, FiFile } from 'react-icons/fi'
import toast from 'react-hot-toast'
import { DateTime } from 'luxon'
import axios from 'axios'
import { WORLD_TIMEZONES, searchTimezones } from '../../utils/timezones'

const COLORS = [
  { name: 'yellow', bg: 'bg-yellow-100', border: 'border-yellow-300' },
  { name: 'blue', bg: 'bg-blue-100', border: 'border-blue-300' },
  { name: 'red', bg: 'bg-red-100', border: 'border-red-300' },
  { name: 'green', bg: 'bg-green-100', border: 'border-green-300' },
  { name: 'purple', bg: 'bg-purple-100', border: 'border-purple-300' },
  { name: 'pink', bg: 'bg-pink-100', border: 'border-pink-300' },
  { name: 'orange', bg: 'bg-orange-100', border: 'border-orange-300' }
]

// Get browser's timezone
const getBrowserTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone
  } catch (error) {
    return 'UTC'
  }
}

export default function NoteEditor({ note, isNew, onSave, onClose, isAdmin, user, passwordUnlocked = true }) {
  const [title, setTitle] = useState('')
  const [content, setContent] = useState('')
  const [color, setColor] = useState('yellow')
  const [scheduleDate, setScheduleDate] = useState('')
  const [scheduledTime, setScheduledTime] = useState('')
  const [timezone, setTimezone] = useState(() => user?.preferredTimezone || getBrowserTimezone())
  const [saving, setSaving] = useState(false)
  const [timezoneSearch, setTimezoneSearch] = useState('')
  const [showTimezoneDropdown, setShowTimezoneDropdown] = useState(false)
  
  // Media upload states
  const [uploadedImages, setUploadedImages] = useState([])
  const [uploadedVideo, setUploadedVideo] = useState(null)
  const [uploadedPDFs, setUploadedPDFs] = useState([])
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)

  // Filter timezones based on search
  const filteredTimezones = timezoneSearch 
    ? searchTimezones(timezoneSearch) 
    : WORLD_TIMEZONES

  useEffect(() => {
    if (note && !isNew) {
      setTitle(note.title)
      setContent(note.content)
      setColor(note.color || 'yellow')
      setTimezone(note.timezone || getBrowserTimezone())
      
      // Set timezone search text to display the timezone name
      const selectedTimezone = WORLD_TIMEZONES.find(tz => tz.value === (note.timezone || getBrowserTimezone()))
      setTimezoneSearch(selectedTimezone ? selectedTimezone.label : (note.timezone || ''))
      
      // Use scheduleDate and scheduledTime directly as strings
      // These are already in the correct format (YYYY-MM-DD and HH:MM)
      if (note.scheduleDate) {
        setScheduleDate(note.scheduleDate)
      }
      if (note.scheduledTime) {
        setScheduledTime(note.scheduledTime)
      }
      
      // Load existing media
      if (note.images) {
        setUploadedImages(note.images)
      }
      if (note.video && note.video.length > 0) {
        // Ensure video is always stored as an array for consistency
        setUploadedVideo(Array.isArray(note.video) ? note.video : [note.video])
      } else {
        setUploadedVideo([])
      }
      if (note.attachments) {
        setUploadedPDFs(note.attachments)
      }
    } else if (isNew) {
      setTitle('')
      setContent('')
      setColor('yellow')
      setScheduleDate('')
      setScheduledTime('')
      const defaultTz = user?.preferredTimezone || getBrowserTimezone()
      setTimezone(defaultTz)
      
      // Set timezone search text for display
      const selectedTimezone = WORLD_TIMEZONES.find(tz => tz.value === defaultTz)
      setTimezoneSearch(selectedTimezone ? selectedTimezone.label : defaultTz)
      
      // Clear media
      setUploadedImages([])
      setUploadedVideo([])
      setUploadedPDFs([])
    }
  }, [note, isNew, user])

  const handleSave = async () => {
    if (!title.trim()) {
      toast.error('Please enter a note title')
      return
    }
    setSaving(true)
    try {
      await onSave({
        title: title.trim(),
        content,
        color,
        timezone,
        scheduleDate: scheduleDate || null,
        scheduleTime: scheduledTime || null
      })
    } finally { setSaving(false) }
  }

  // Helper to get password verification headers
  const getPasswordHeaders = () => {
    const headers = {}
    if (passwordUnlocked) {
      headers['x-notepad-password-verified'] = 'true'
    }
    return headers
  }

  // Handle image/video/pdf file selection
  const handleMediaSelect = async (e) => {
    const files = Array.from(e.target.files || [])
    if (files.length === 0) return

    if (!note || !note._id) {
      toast.error('Please save the note first before adding media')
      return
    }

    setUploading(true)
    setUploadProgress(0)

    try {
      const formData = new FormData()
      files.forEach(file => {
        formData.append('media', file)
      })

      const response = await axios.post(`/notes/${note._id}/media`, formData, {
        headers: { 
          'Content-Type': 'multipart/form-data',
          ...getPasswordHeaders()
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
          setUploadProgress(progress)
        }
      })

      // Update media state with response
      setUploadedImages(response.data.note.images || [])
      setUploadedVideo(response.data.note.video || [])
      setUploadedPDFs(response.data.note.attachments || [])
      
      toast.success(`Media uploaded successfully`)
    } catch (error) {
      console.error('Error uploading media:', error)
      toast.error(error.response?.data?.message || 'Failed to upload media')
    } finally {
      setUploading(false)
      setUploadProgress(0)
    }
  }

  // Handle media deletion
  const handleDeleteMedia = async (mediaId) => {
    if (!note || !note._id) return

    try {
      // URL-encode mediaId in case it contains slashes (from Cloudinary folder paths)
      const encodedMediaId = encodeURIComponent(mediaId)
      const response = await axios.delete(`/notes/${note._id}/media/${encodedMediaId}`, {
        headers: getPasswordHeaders()
      })
      
      setUploadedImages(response.data.note.images || [])
      setUploadedVideo(response.data.note.video || [])
      setUploadedPDFs(response.data.note.attachments || [])
      
      toast.success('Media deleted successfully')
    } catch (error) {
      console.error('Error deleting media:', error)
      toast.error(error.response?.data?.message || 'Failed to delete media')
    }
  }

  const canEdit = isAdmin || isNew

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-900">{isNew ? 'New Note' : 'Edit Note'}</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <FiX className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Note title..."
              className="input-field text-lg"
              maxLength={255}
              disabled={!canEdit}
            />
            <p className="text-xs text-gray-500 mt-1">{title.length}/255</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Content</label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Write your note here..."
              className="input-field min-h-64 p-4 font-mono text-sm resize-none"
              disabled={!canEdit}
            />
            <p className="text-xs text-gray-500 mt-1">{content.length} characters</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">Color</label>
            <div className="flex gap-3 flex-wrap">
              {COLORS.map((c) => (
                <button
                  key={c.name}
                  onClick={() => canEdit && setColor(c.name)}
                  disabled={!canEdit}
                  className={`w-10 h-10 rounded-full border-4 transition-transform ${c.bg} ${
                    color === c.name ? `${c.border} scale-110` : 'border-gray-300'
                  } ${!canEdit ? 'opacity-50 cursor-not-allowed' : ''}`}
                  title={c.name}
                />
              ))}
            </div>
          </div>

          {/* Media Upload Section */}
          {canEdit && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">📸 Media & Attachments</label>
              
              {isNew && (
                <p className="text-xs text-amber-600 mb-3 p-2 bg-amber-50 rounded border border-amber-200">
                  💡 Create and save your note first, then you can add images, videos, and attachments.
                </p>
              )}
              
              {/* Upload Input - only show if note exists */}
              {!isNew && (
                <>
                  <div className="mb-4">
                    <div className="flex gap-2">
                      <label className="flex-1 cursor-pointer">
                        <div className={`border-2 border-dashed border-blue-300 rounded-lg p-6 text-center hover:bg-blue-50 transition-colors ${
                          uploading ? 'opacity-50 cursor-not-allowed' : ''
                        }`}>
                          <FiUpload className="w-6 h-6 mx-auto text-blue-600 mb-2" />
                          <p className="text-sm font-medium text-gray-700">Click to upload media</p>
                          <p className="text-xs text-gray-500 mt-1">Images: JPG, PNG, WEBP</p>
                          <p className="text-xs text-gray-500">Videos: all standard video formats (MP4, MOV, AVI, MKV, WEBM, FLV, WMV, M4V, MPEG, OGG, 3GP)</p>
                          <p className="text-xs text-gray-500">Attachments: PDF</p>
                        </div>
                        <input
                          type="file"
                          multiple
                          accept="image/*,video/*,application/pdf"
                          onChange={handleMediaSelect}
                          disabled={uploading || !canEdit}
                          className="hidden"
                        />
                      </label>
                    </div>
                    
                    {uploading && (
                      <div className="mt-2">
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-blue-600 h-2 rounded-full transition-all"
                            style={{ width: `${uploadProgress}%` }}
                          />
                        </div>
                        <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                          <FiLoader className="w-4 h-4 animate-spin" /> Uploading media... {uploadProgress}%
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Images Preview */}
                  {uploadedImages.length > 0 && (
                    <div className="mb-4">
                      <p className="text-xs font-medium text-gray-600 mb-2">📸 Images ({uploadedImages.length})</p>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-1 sm:gap-2">
                        {uploadedImages.map((img) => (
                          <div key={img.publicId} className="relative group">
                            <img 
                              src={img.url} 
                              alt="Uploaded" 
                              className="w-full h-24 object-cover rounded-lg border border-gray-200"
                            />
                            <button
                              onClick={() => handleDeleteMedia(img.publicId)}
                              className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                              title="Delete image"
                            >
                              <FiTrash2 className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Videos List */}
                  {uploadedVideo && uploadedVideo.length > 0 && (
                    <div className="mb-4">
                      <p className="text-xs font-medium text-gray-600 mb-2">🎥 Videos ({uploadedVideo.length})</p>
                      <div className="space-y-2">
                        {uploadedVideo.map((vid, index) => (
                          <div key={vid.publicId || index} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg border border-gray-200">
                            <span className="text-sm text-gray-700">Video {index + 1}</span>
                            <button
                              onClick={() => handleDeleteMedia(vid.publicId)}
                              className="text-red-500 hover:text-red-700 transition-colors"
                              title="Delete video"
                            >
                              <FiTrash2 className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* PDFs/Attachments List */}
                  {uploadedPDFs && uploadedPDFs.length > 0 && (
                    <div className="mb-4">
                      <p className="text-xs font-medium text-gray-600 mb-2">📎 Attachments ({uploadedPDFs.length})</p>
                      <div className="space-y-2">
                        {uploadedPDFs.map((pdf, index) => (
                          <div key={pdf.publicId || index} className="flex items-center justify-between bg-blue-50 p-3 rounded-lg border border-blue-200">
                            <div className="flex items-center gap-2">
                              <FiFile className="w-4 h-4 text-blue-600" />
                              <span className="text-sm text-gray-700">{pdf.filename || `Attachment ${index + 1}`}</span>
                            </div>
                            <button
                              onClick={() => handleDeleteMedia(pdf.publicId)}
                              className="text-red-500 hover:text-red-700 transition-colors"
                              title="Delete attachment"
                            >
                              <FiTrash2 className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Timezone</label>
            <div className="relative">
              <div className="flex gap-2">
                <div className="flex-1 relative">
                  <FiSearch className="absolute left-3 top-3 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search timezone (e.g., New York, Tokyo, London)..."
                    value={timezoneSearch}
                    onChange={(e) => {
                      setTimezoneSearch(e.target.value)
                      setShowTimezoneDropdown(true)
                    }}
                    onFocus={() => setShowTimezoneDropdown(true)}
                    className="input-field pl-10 text-sm"
                  />
                </div>
              </div>
              
              {/* Timezone Dropdown */}
              {showTimezoneDropdown && filteredTimezones.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-64 overflow-y-auto">
                  {filteredTimezones.slice(0, 100).map((tz) => (
                    <button
                      key={tz.value}
                      onClick={() => {
                        setTimezone(tz.value)
                        setTimezoneSearch(tz.label)
                        setShowTimezoneDropdown(false)
                      }}
                      className={`w-full text-left px-4 py-2 hover:bg-blue-50 transition-colors border-b border-gray-100 last:border-b-0 ${
                        timezone === tz.value ? 'bg-blue-100 font-medium' : ''
                      }`}
                    >
                      <div className="flex justify-between items-center">
                        <span className="text-sm">{tz.label}</span>
                        {timezone === tz.value && <span className="text-blue-600 text-sm">✓</span>}
                      </div>
                    </button>
                  ))}
                  {filteredTimezones.length > 100 && (
                    <div className="px-4 py-2 text-xs text-gray-500 text-center">
                      Showing 100 of {filteredTimezones.length} results
                    </div>
                  )}
                </div>
              )}

              {timezoneSearch && filteredTimezones.length === 0 && showTimezoneDropdown && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-4 text-center text-gray-500 text-sm">
                  No timezones found
                </div>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-2">Selected: <strong>{timezone}</strong></p>
          </div>

          {/* Close dropdown when clicking outside */}
          {showTimezoneDropdown && (
            <div 
              className="fixed inset-0 z-40" 
              onClick={() => setShowTimezoneDropdown(false)}
            />
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Schedule Date (Optional)</label>
            <p className="text-xs text-gray-500 mb-3">
              📅 <strong>Date-based scheduling:</strong> The note will be available from <strong>00:00 (start of day)</strong> on this date in your timezone. The reminder email will be sent automatically at the start of the day.
            </p>
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <input
                  type="date"
                  value={scheduleDate}
                  onChange={(e) => canEdit && setScheduleDate(e.target.value)}
                  className="input-field"
                  disabled={!canEdit}
                  title="Note will become available from 00:00 on this date in your timezone"
                />
              </div>
              <div className="text-sm text-gray-600 whitespace-nowrap">
                📍 {timezone}
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Reminder Time (Optional)</label>
            <p className="text-xs text-gray-500 mb-3">
              ⏰ <strong>Optional:</strong> Specify a time for display in reminder emails only. This does <strong>not</strong> affect when the note becomes available—that is always 00:00 on the scheduled date.
            </p>
            <input
              type="time"
              value={scheduledTime}
              onChange={(e) => canEdit && setScheduledTime(e.target.value)}
              className="input-field"
              disabled={!canEdit || !scheduleDate}
              placeholder="00:00"
              title="Time to display in reminder email (does not affect note availability)"
            />
          </div>

          {scheduleDate && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="text-sm text-green-800 font-medium mb-2">📅 Schedule Confirmed</p>
              <p className="text-sm text-green-700 mb-1">
                <strong>Availability Date:</strong> {scheduleDate} at <strong>00:00</strong> (start of day in {timezone})
              </p>
              {scheduledTime && (
                <p className="text-sm text-green-700 mb-1">
                  <strong>Reminder Email Time:</strong> {scheduledTime} (displayed in notification)
                </p>
              )}
              <p className="text-xs text-green-600 mt-2">
                ✓ Note will be available from the start of the scheduled date in your timezone.
                A reminder email will be sent automatically.
              </p>
            </div>
          )}

          {!canEdit && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-700">
                ℹ️ You cannot edit this note. Only admins can edit and delete existing notes.
              </p>
            </div>
          )}
        </div>

        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
          <button onClick={onClose} className="btn-secondary">Cancel</button>
          {canEdit && (
            <button
              onClick={handleSave}
              disabled={saving}
              className="btn-primary flex items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <FiLoader className="w-5 h-5 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <FiSave className="w-5 h-5" />
                  Save
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}